(() => {
  const script = document.currentScript;
  if (!(script instanceof Element)) return;

  const BLOCK_EVENT = 'sigma:mystery-credit-blocked';
  const POLICY_SELECTOR = '[data-mystery-credit-policy]';
  const PRIVATE_LINE_SELECTOR = [
    '[data-cart-drawer] [data-private-mystery-hold]',
    '[data-cart-page] [data-private-mystery-hold]',
  ].join(', ');
  const policyUrl = script.dataset.policyUrl
    ? new URL(script.dataset.policyUrl, window.location.origin).href
    : '';
  const wrappedCheckouts = new WeakMap();
  const proxiedProviders = new WeakMap();
  let providerCheckoutInFlight = null;
  let uiSyncQueued = false;

  const showNotices = (scope = document) => {
    const scopedNotices = typeof scope.querySelectorAll === 'function'
      ? scope.querySelectorAll('[data-mystery-credit-notice]')
      : [];
    const notices = scopedNotices.length
      ? scopedNotices
      : document.querySelectorAll('[data-mystery-credit-notice]');
    notices.forEach((notice) => { notice.hidden = false; });
  };

  const showError = (message) => {
    document.querySelectorAll('[data-cart-global-error]').forEach((target) => {
      target.textContent = message;
      target.hidden = false;
      target.setAttribute('role', 'alert');
    });
  };

  const dispatchBlock = (reason, message) => {
    if (reason === 'private-mystery-hold') showNotices();
    else showError(message || 'Store Credit is temporarily unavailable. You can still check out without it.');
    document.dispatchEvent(new CustomEvent(BLOCK_EVENT, {
      detail: { message: message || '', reason },
    }));
    return false;
  };

  const readPolicy = async () => {
    if (!policyUrl) throw new Error('Missing mystery credit policy URL.');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    let html;
    try {
      const response = await window.fetch(policyUrl, {
        method: 'GET', cache: 'no-store', credentials: 'same-origin',
        headers: { Accept: 'text/html' }, signal: controller.signal,
      });
      if (!response.ok) throw new Error('Mystery credit policy request failed.');
      html = await response.text();
    } finally {
      clearTimeout(timeout);
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const markers = parsed.querySelectorAll(POLICY_SELECTOR);
    if (markers.length !== 1) throw new Error('Mystery credit policy response was ambiguous.');
    const policy = markers[0].getAttribute('data-mystery-credit-policy');
    if (policy !== 'allowed' && policy !== 'blocked') {
      throw new Error('Mystery credit policy response was invalid.');
    }
    return policy;
  };

  const guardCheckout = (checkout) => {
    if (typeof checkout !== 'function') return checkout;
    if (wrappedCheckouts.has(checkout)) return wrappedCheckouts.get(checkout);

    const guardedCheckout = function (...args) {
      // Only the dedicated page may initiate credit spending. A cart button,
      // floating launcher or automatic URL trigger must never create a credit draft.
      const event = args[0];
      event?.preventDefault?.();
      const action = event?.target instanceof Element
        ? event.target.closest('[data-store-credit-page] .creditsyard-apply-credit-button')
        : null;
      if (!action || !action.isConnected) {
        const widgetAction = event?.target instanceof Element
          ? event.target.closest('.creditsyard-apply-credit-button, .store-credit-embedded-button')
          : null;
        if (widgetAction && script.dataset.creditPageUrl) {
          window.location.assign(script.dataset.creditPageUrl);
        }
        return false;
      }
      if (providerCheckoutInFlight) return providerCheckoutInFlight;
      const receiver = this;
      const attempt = (async () => {
        let policy;
        try {
          policy = await readPolicy();
        } catch (error) {
          return dispatchBlock('policy-unavailable');
        }
        if (policy === 'blocked') return dispatchBlock('private-mystery-hold');
        return Reflect.apply(checkout, receiver, args);
      })();
      providerCheckoutInFlight = attempt;
      void attempt.finally(() => {
        if (providerCheckoutInFlight === attempt) providerCheckoutInFlight = null;
      });
      return attempt;
    };

    wrappedCheckouts.set(checkout, guardedCheckout);
    wrappedCheckouts.set(guardedCheckout, guardedCheckout);
    return guardedCheckout;
  };

  const instrumentProvider = (provider) => {
    if (!provider || (typeof provider !== 'object' && typeof provider !== 'function')) return provider;
    if (proxiedProviders.has(provider)) return proxiedProviders.get(provider);

    // The provider writes this selector before its initial replacement pass and
    // reads it again on a timer. A valid selector matching nothing prevents both
    // passes while preserving the theme's original nodes and event handlers.
    Object.defineProperty(provider, 'checkout_button_selector', {
      configurable: true,
      enumerable: true,
      get() { return ':not(*)'; },
      set() {},
    });

    const originalCheckout = provider.checkout;
    if (typeof originalCheckout === 'function') {
      const guardedCheckout = guardCheckout(originalCheckout);
      provider.checkout = guardedCheckout;
      if (provider.applyStoreCredit === originalCheckout) provider.applyStoreCredit = guardedCheckout;
    }

    const proxy = new Proxy(provider, {
      defineProperty(target, property, descriptor) {
        const nextDescriptor = { ...descriptor };
        if (property === 'checkout' && 'value' in nextDescriptor) {
          nextDescriptor.value = guardCheckout(nextDescriptor.value);
        }
        return Reflect.defineProperty(target, property, nextDescriptor);
      },
      set(target, property, value) {
        return Reflect.set(target, property, property === 'checkout' ? guardCheckout(value) : value);
      },
    });
    proxiedProviders.set(provider, proxy);
    proxiedProviders.set(proxy, proxy);
    return proxy;
  };

  let providerValue = instrumentProvider(window.StoreCredit);
  const providerDescriptor = Object.getOwnPropertyDescriptor(window, 'StoreCredit');
  if (!providerDescriptor || providerDescriptor.configurable) {
    Object.defineProperty(window, 'StoreCredit', {
      configurable: true,
      enumerable: true,
      get() { return providerValue; },
      set(value) { providerValue = instrumentProvider(value); },
    });
  }

  const syncUi = () => {
    uiSyncQueued = false;
    const blocked = Boolean(document.querySelector(PRIVATE_LINE_SELECTOR));
    document.querySelectorAll('[data-mystery-credit-notice]').forEach((notice) => {
      const root = notice.closest('[data-cart-drawer], [data-cart-page]');
      notice.hidden = !Boolean(root?.querySelector('[data-private-mystery-hold]'));
    });
    return blocked;
  };

  const queueUiSync = () => {
    if (uiSyncQueued) return;
    uiSyncQueued = true;
    void Promise.resolve().then(syncUi);
  };

  const guard = {
    blockEvent: BLOCK_EVENT,
    checkPolicy: readPolicy,
    syncUi,
  };
  window.SigmaMysteryCreditGuard = guard;

  new MutationObserver(queueUiSync).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  queueUiSync();
})();
